import React from 'react';
import { 
  ArrowLeft, 
  BarChart2, 
  Code, 
  Lock, 
  Layers, 
  Terminal, 
  Check,
  ArrowRight
} from 'lucide-react';

const DataVisualizationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-6 py-16">
          <a href="/projects" className="inline-flex items-center text-blue-100 hover:text-white mb-8">
            <ArrowLeft className="mr-2" size={16} /> Back to Projects
          </a>
          <h1 className="text-4xl font-bold mb-4">Data Visualization Team Improvements</h1>
          <div className="flex flex-col md:flex-row md:items-center text-blue-100 mb-6">
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <BarChart2 size={18} className="mr-2" /> 
              <span>Data Visualization</span>
            </div>
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Code size={18} className="mr-2" /> 
              <span>API Architecture</span>
            </div>
            <div className="flex items-center">
              <Lock size={18} className="mr-2" /> 
              <span>Security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-blue-50 p-8">
              <h2 className="text-2xl font-bold mb-6 text-blue-900">Project Overview</h2>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Timeline</h3>
                <p className="text-gray-700">October 2021 - June 2022</p>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Role</h3>
                <p className="text-gray-700">Software Engineer Team Lead</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">CI/CD Pipelines</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">API Design</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Security Tools</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Katalon</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Selenium</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Fortify</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">WebInspect</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">DbProtect</span>
                </div>
              </div>
            </div>
            <div className="md:w-2/3 p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">The Challenge</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                The Data Visualization team was facing several technical challenges that were impacting 
                development velocity, system reliability, and security compliance. Key issues included:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Immature deployment pipelines leading to inconsistent releases</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Tight coupling between UI and API components causing deployment dependencies</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Inconsistent permission system implementation across the application</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Lack of comprehensive testing framework for both unit and integration tests</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Security vulnerabilities identified in regular audits but lacking systematic remediation</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The goal was to implement comprehensive technical improvements across the entire application 
                stack, improving code quality, deployment reliability, and security while enabling the team 
                to maintain feature development velocity.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="container mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">The Solution</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Terminal className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Pipeline Maturity</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Enhanced CI/CD pipelines with automated testing gates</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented environment-specific configuration management</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created deployment validation checks to prevent broken releases</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Code className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">API Refactoring</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Standardized API structure with consistent RESTful patterns</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Decoupled UI and API for independent deployment</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Reduced code complexity through modular architecture</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Layers className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Permission System</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Refactored authentication integration with Portal team</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented consistent role-based access control throughout the application</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Centralized permission validation to eliminate security gaps</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Lock className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Security Enhancements</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Executed Fortify, WebInspect, and DbProtect security scans</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created prioritized remediation plans for identified vulnerabilities</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Integrated security scanning into the CI/CD pipeline</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Testing Framework */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-12">
          <div className="flex items-center mb-6">
            <div className="p-2 bg-purple-100 rounded-full mr-3">
              <BarChart2 className="text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Testing Framework Implementation</h3>
          </div>
          
          <p className="text-gray-700 mb-6">
            A key achievement was establishing a comprehensive testing framework to ensure application 
            quality and prevent regressions. Working closely with the QA lead, we implemented:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-lg mb-2 text-gray-800">Unit Testing</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Component-level tests for UI elements</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Service and controller tests for API</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Mock services for external dependencies</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-lg mb-2 text-gray-800">Integration Testing</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Katalon test suites for UI workflows</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Selenium scripts for automated UI testing</span>
                </li>
                <li className="flex items-start">
                  <Check size={16} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">API integration tests for end-to-end validation</span>
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-gray-700">
            The testing framework was integrated into the CI/CD pipeline, with automatic test execution 
            on each code commit and comprehensive test runs before deployment to any environment.
          </p>
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Results & Impact</h2>
          
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Key Outcomes</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="border-t-4 border-purple-500 pt-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Deployment Reliability</h4>
                <p className="text-gray-700">
                  Reduced deployment failures by 92% and cut average deployment time from 2 hours to 
                  25 minutes through improved pipeline automation and validation.
                </p>
              </div>
              
              <div className="border-t-4 border-purple-500 pt-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Code Quality</h4>
                <p className="text-gray-700">
                  Decreased API defect rate by 65% through standardization, and improved maintainability 
                  scores by 40% as measured by static code analysis tools.
                </p>
              </div>
              
              <div className="border-t-4 border-purple-500 pt-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Security Posture</h4>
                <p className="text-gray-700">
                  Remediated 97% of high and critical security vulnerabilities, and introduced proactive 
                  security scanning that reduced new vulnerability introduction by 78%.
                </p>
              </div>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-3 text-gray-800">Team Productivity</h4>
              <p className="text-gray-700 mb-4">
                The technical improvements delivered significant productivity benefits for the development team:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check size={18} className="mr-2 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Independent UI and API deployments enabled parallel work streams</span>
                </li>
                <li className="flex items-start">
                  <Check size={18} className="mr-2 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Automated testing reduced QA cycle time by 60%</span>
                </li>
                <li className="flex items-start">
                  <Check size={18} className="mr-2 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Standardized API design improved cross-team collaboration</span>
                </li>
                <li className="flex items-start">
                  <Check size={18} className="mr-2 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Proactive security scanning eliminated late-stage security fixes</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Key Learnings */}
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Key Learnings</h3>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">DevOps Culture Shift</h4>
                <p className="text-gray-700">
                  The most significant challenge wasn't technical but cultural. Building a DevOps mindset 
                  required consistent education and demonstration of benefits to gain team buy-in. Starting 
                  with small wins built momentum for larger changes.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Test-Driven Approach</h4>
                <p className="text-gray-700">
                  Introducing testing after code was already written proved challenging. For future projects, 
                  a test-driven development approach would be more effective, writing tests before or alongside 
                  the application code.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Incremental Refactoring</h4>
                <p className="text-gray-700">
                  Breaking down the API refactoring into smaller, incremental changes allowed us to deliver 
                  continuous improvements without disrupting ongoing development. This approach reduced risk 
                  and provided immediate benefits throughout the project.
                </p>
              </div>
            </div>
          </div>
          
          {/* Next Case Study Navigation */}
          <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-md">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Next Case Study
              </h3>
              <p className="text-gray-600">
                Cloud Automation at Syntasa
              </p>
            </div>
            <a
              href="/projects/cloud-automation"
              className="mt-4 md:mt-0 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition duration-300 flex items-center"
            >
              View Next Project <ArrowRight className="ml-2" size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataVisualizationCaseStudy;